import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName } from "@/utils/displayName";
import { normalizeImageUrl } from "@/lib/image-url";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { ArrowLeft, MessageCircle, Edit, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";

interface MeetingPostDetailResponse {
  ok: boolean;
  data: {
    meetingId: string;
    meetingTitle: string;
    post: {
      id: string;
      title: string | null;
      content: string;
      images: string[];
      category: string;
      likes: number;
      comments: number;
      authorNickname: string;
      authorDisplayName: string;
      authorId: string | null;
      createdAt: string;
    };
  };
}

export default function MeetingPostDetailPage() {
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ id: string; postId: string }>(
    "/meetings/:id/posts/:postId"
  );
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const meetingId = params?.id;
  const postId = params?.postId;

  // postId가 "new"이면 게시글 작성 페이지로 리다이렉트 (useEffect로 처리하여 무한 루프 방지)
  useEffect(() => {
    if (postId === "new" && meetingId) {
      navigate(`/meetings/${meetingId}/posts/new`);
    }
  }, [postId, meetingId, navigate]);

  // postId가 "new"이면 쿼리를 실행하지 않음
  const { data, isLoading, isError } = useQuery<MeetingPostDetailResponse>({
    queryKey: ["/api/meetings", meetingId, "posts", postId],
    queryFn: async () => {
      if (!meetingId || !postId) throw new Error("Invalid request");
      // postId가 "new"이면 에러 발생 (이미 위에서 처리했지만 이중 체크)
      if (postId === "new") throw new Error("Invalid post ID");
      const res = await fetch(`/api/meetings/${meetingId}/posts/${postId}`);
      if (!res.ok) throw new Error("Failed to load post");
      return res.json();
    },
    enabled: !!meetingId && !!postId && postId !== "new",
  });

  // postId가 "new"이면 리다이렉트 중이므로 아무것도 렌더링하지 않음
  if (postId === "new") {
    return null;
  }

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Card className="p-8 text-center max-w-md">
          <p className="text-muted-foreground mb-4">
            유효하지 않은 게시글 주소입니다.
          </p>
          <Button onClick={() => navigate("/meetings")}>모임 목록으로</Button>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <p className="text-muted-foreground">로딩 중...</p>
      </div>
    );
  }

  if (isError || !data?.ok) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Card className="p-8 text-center max-w-md">
          <p className="text-muted-foreground mb-4">
            게시글 정보를 불러오지 못했습니다.
          </p>
          <Button onClick={() => navigate(`/meetings/${meetingId}`)}>
            모임으로 돌아가기
          </Button>
        </Card>
      </div>
    );
  }

  // API 응답 구조 처리 (다양한 형식 지원)
  // 형식 1: { ok: true, data: { meetingId, meetingTitle, post: {...} } }
  // 형식 2: { ok: true, data: { data: { meetingId, meetingTitle, post: {...} } } }
  // 형식 3: { meetingId, meetingTitle, post: {...} } (직접)
  let payload: any = null;
  let post: any = null;
  let meetingTitle = "모임";

  if (data.ok && data.data) {
    // 형식 1 또는 형식 2
    if (data.data.post) {
      payload = data.data;
      post = data.data.post;
      meetingTitle = data.data.meetingTitle || "모임";
    } else if ((data.data as any).data?.post) {
      payload = (data.data as any).data;
      post = (data.data as any).data.post;
      meetingTitle = (data.data as any).data.meetingTitle || "모임";
    } else if ((data.data as any).post) {
      // 중첩된 경우
      payload = data.data;
      post = (data.data as any).post;
      meetingTitle = (data.data as any).meetingTitle || "모임";
    }
  } else if ((data as any).post) {
    // 형식 3: 직접 post 객체
    payload = data as any;
    post = (data as any).post;
    meetingTitle = (data as any).meetingTitle || "모임";
  }

  // 디버깅 로그
  if (!post) {
    console.error("[MeetingPostDetailPage] Post data not found:", {
      data,
      dataOk: data.ok,
      dataData: data.data,
      dataDataPost: data.data?.post,
      dataDataData: (data.data as any)?.data,
    });
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Card className="p-8 text-center max-w-md">
          <p className="text-muted-foreground mb-4">
            게시글 데이터를 찾을 수 없습니다.
          </p>
          <Button onClick={() => navigate(`/meetings/${meetingId}`)}>
            모임으로 돌아가기
          </Button>
        </Card>
      </div>
    );
  }

  const createdAtDate = post.createdAt ? new Date(post.createdAt) : null;

  // 작성자 확인
  const isAuthor = user && post.authorId && user.uid === post.authorId;

  // 게시글 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(
        `/api/meetings/${meetingId}/posts/${postId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "게시글 삭제에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "게시글 삭제 완료",
        description: "게시글이 삭제되었습니다.",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/meetings", meetingId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/meetings", meetingId, "posts"],
      });
      navigate(`/meetings/${meetingId}`);
    },
    onError: (error: Error) => {
      toast({
        title: "게시글 삭제 실패",
        description: error.message || "게시글 삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/meetings/${meetingId}`)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-sm text-muted-foreground">모임</p>
              <p className="font-medium">{meetingTitle}</p>
            </div>
          </div>
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/meetings/${meetingId}/posts/new`)}
            >
              글쓰기
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">
                    {createdAtDate
                      ? formatDistanceToNow(createdAtDate, {
                          addSuffix: true,
                          locale: ko,
                        })
                      : "작성일 정보 없음"}
                  </p>
                  {post.title && (
                    <h1 className="text-2xl font-semibold">{post.title}</h1>
                  )}
                  <p className="text-sm text-muted-foreground">
                    작성자: {getDisplayName(post)}
                  </p>
                </div>
                {isAuthor && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/meetings/${meetingId}/posts/${postId}/edit`)
                      }
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      수정
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      삭제
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {post.images && post.images.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {post.images.map((image: string, idx: number) => {
                  const normalized = normalizeImageUrl(image) || image;
                  return (
                    <div
                      key={`${image}-${idx}`}
                      className="rounded-xl overflow-hidden bg-muted"
                    >
                      <img
                        src={normalized}
                        alt={`게시글 이미지 ${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="whitespace-pre-wrap text-base leading-relaxed">
              {post.content}
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground border-t pt-4">
              <div>카테고리: {post.category}</div>
              <div>👍 {post.likes}</div>
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                {post.comments}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>게시글 삭제</AlertDialogTitle>
            <AlertDialogDescription>
              정말 이 게시글을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate();
                setShowDeleteDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
